import type { CSSProperties } from "react";

export const PRIMARY_COLOR = "#D7472F";
export const PAPER_COLOR = "#faf7f2";
const INK_COLOR = "#202521";

const normalize = (color: string) => {
  if (/^#[\da-f]{3}$/i.test(color)) {
    return `#${[...color.slice(1)].map((channel) => channel.repeat(2)).join("")}`;
  }
  return /^#[\da-f]{6}$/i.test(color) ? color : PRIMARY_COLOR;
};
const channels = (color: string) =>
  normalize(color)
    .slice(1)
    .match(/.{2}/g)!
    .map((value) => parseInt(value, 16));

export function contrastRatio(first: string, second: string): number {
  const luminance = (color: string) => {
    const [r, g, b] = channels(color).map((value) => {
      const s = value / 255;
      return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
  };
  const [lighter, darker] = [luminance(first), luminance(second)].sort(
    (a, b) => b - a,
  );
  return (lighter! + 0.05) / (darker! + 0.05);
}

export function brandTextColor(color: string): string {
  let rgb = channels(color);
  const hex = () =>
    `#${rgb.map((value) => value.toString(16).padStart(2, "0")).join("")}`;
  // A small safety margin above AA also covers the warm paper background.
  while (contrastRatio(hex(), PAPER_COLOR) < 4.6) {
    rgb = rgb.map((value) => Math.floor(value * 0.94));
  }
  return hex();
}

export function brandForeground(color: string): string {
  if (contrastRatio(color, "#ffffff") >= 4.6) return "#ffffff";
  return contrastRatio(color, INK_COLOR) >= 4.6 ? INK_COLOR : "#000000";
}

export function tenantTheme(
  primary = PRIMARY_COLOR,
  secondary = INK_COLOR,
): CSSProperties {
  const brand = normalize(primary);
  return {
    "--brand": brand,
    "--brand-secondary": normalize(secondary),
    "--brand-ink": brandTextColor(brand),
    "--on-brand": brandForeground(brand),
  } as CSSProperties;
}
